// Ponto de equilíbrio — "quanto preciso vender pra não ter prejuízo?"
// Sem divisão pro-cliente/minha-cópia (controle interno). Cartão único.
// docs/calculamei-spec-calculadoras.md — seção 5.

export interface PontoEquilibrioCustoFixo {
  descricao: string
  valor: number
}

export interface PontoEquilibrioInputs {
  /** Lista de custos fixos { descrição, valor } — aluguel, internet, DAS, etc. */
  custosFixos: PontoEquilibrioCustoFixo[]
  /** Preço médio de venda do produto/serviço. */
  precoVenda: number
  /** Custo por unidade vendida (material, comissão, imposto/un). */
  custoVariavel: number
}

export interface PontoEquilibrioResultado {
  custosFixosTotal: number
  /** Margem de contribuição em R$ por unidade (precoVenda − custoVariavel). */
  margemContribuicao: number
  /** Margem de contribuição em % do preço. */
  margemContribuicaoPerc: number
  /**
   * Bloqueio: preço ≤ custo variável → cada venda perde dinheiro e o PE
   * nunca é atingido. Quando true, peUnidades/peReais vêm null.
   */
  prejuizoPorUnidade: boolean
  /** Quantas unidades vender por período (arredondado pra CIMA). null se bloqueado. */
  peUnidades: number | null
  /** Faturamento equivalente em R$. null se bloqueado. */
  peReais: number | null
}

const round2 = (n: number) => Number(n.toFixed(2))

export function calcularPontoEquilibrio(
  inputs: PontoEquilibrioInputs
): PontoEquilibrioResultado {
  const { custosFixos, precoVenda, custoVariavel } = inputs

  const custosFixosTotal = custosFixos.reduce((acc, c) => acc + (c.valor || 0), 0)
  const margemContribuicao = precoVenda - custoVariavel
  const margemContribuicaoPerc =
    precoVenda > 0 ? (margemContribuicao / precoVenda) * 100 : 0

  // Preço abaixo (ou igual) ao custo variável: nunca cobre os fixos.
  const prejuizoPorUnidade = margemContribuicao <= 0

  // peUnidades sempre pra CIMA — não dá pra vender 166,7 unidades.
  const peUnidades = prejuizoPorUnidade
    ? null
    : Math.ceil(custosFixosTotal / margemContribuicao)

  // Calculado a partir dos valores não-arredondados pra preservar precisão.
  const peReais = prejuizoPorUnidade
    ? null
    : round2((custosFixosTotal * precoVenda) / margemContribuicao)

  return {
    custosFixosTotal: round2(custosFixosTotal),
    margemContribuicao: round2(margemContribuicao),
    margemContribuicaoPerc: round2(margemContribuicaoPerc),
    prejuizoPorUnidade,
    peUnidades,
    peReais,
  }
}
